import type { FC, ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useSpecificRoleAccess } from "../hooks/useSpecificRoleAccess";

interface ProtectedRouteProps {
  children: ReactNode;
}

const ProtectedRoute: FC<ProtectedRouteProps> = ({ children }) => {
  const { user, loading } = useAuth();
  const { hasAccess: isSpecificRole, loading: specificRoleLoading } =
    useSpecificRoleAccess();
  const location = useLocation();

  if (loading || specificRoleLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent"></div>
      </div>
    );
  }

  if (!user) {
    // Save the attempted location for redirect after login
    return (
      <Navigate
        to="/authentication/sign-in"
        state={{ from: location }}
        replace
      />
    );
  }

  // If user has role_id 6, redirect them to the appropriate variant page
  if (isSpecificRole) {
    const path = location.pathname;

    // Map regular paths to variant paths
    if (path === "/location") {
      return <Navigate to="/location_variant" replace />;
    }
    if (path === "/contract-creation") {
      return <Navigate to="/contract_creation_variant" replace />;
    }
    // For any other page, redirect to the location_variant page (default page for role_id 6)
    if (
      path !== "/location_variant" &&
      path !== "/contract_creation_variant"
    ) {
      return <Navigate to="/location_variant" replace />;
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;
